import { getUrl } from "aws-amplify/storage";
import type { SearchIndexArtifact } from "./types";

const PREFIX = "search/";
const POINTER_KEY = `${PREFIX}latest.json`;
const URL_TTL_SECONDS = 300;

/** Matches the 15-minute build cadence: a cached copy older than this is stale. */
const CACHE_TTL_MS = 15 * 60 * 1000;

interface LatestPointer {
  buildId: string;
  generatedAt: string;
  docsKey: string;
}

/** The index artifact could not be read. `status` is the S3 response status. */
export class SearchIndexReadError extends Error {
  constructor(
    readonly path: string,
    readonly status: number,
  ) {
    super(`Could not read ${path} (HTTP ${status})`);
    this.name = "SearchIndexReadError";
  }
}

/**
 * True when no build has published an index yet, so callers can fall back
 * quietly rather than reporting a failure.
 *
 * Deliberately scoped to the pointer. A 404 on the documents it references
 * means the pointer outlived its artifact — a broken build, not an absent
 * one — and that must surface as a retryable failure instead of being
 * mistaken for a pristine environment.
 */
export function isMissingSearchIndex(error: unknown): boolean {
  return (
    error instanceof SearchIndexReadError &&
    error.status === 404 &&
    error.path === POINTER_KEY
  );
}

/**
 * Reads the newest search index from storage.
 *
 * Both reads go through a signed URL and `fetch` rather than `downloadData`:
 * the artifacts are stored gzipped, and `downloadData` hands back a Blob that
 * comes up empty for a gzip response whose inflated size outgrows the
 * browser's in-memory buffer — yielding `""` to parse with no error.
 *
 * The `latest.json` pointer is written last by the build, so resolving through
 * it means a build that failed partway can never be read as a mix of two.
 */
export async function loadSearchIndexFromStorage(): Promise<SearchIndexArtifact> {
  const pointer = await read<LatestPointer>(POINTER_KEY);
  const { docs } = await read<Pick<SearchIndexArtifact, "docs">>(
    `${PREFIX}${pointer.docsKey}`,
  );
  return { buildId: pointer.buildId, generatedAt: pointer.generatedAt, docs };
}

/**
 * Reads one artifact, tagging every not-found with the path it came from.
 *
 * `getUrl` can reject with `NoSuchKey` before any fetch happens. Left
 * untagged, that rejection is indistinguishable between the pointer and the
 * documents it references — and only a missing *pointer* means the index was
 * never published.
 */
async function read<T>(path: string): Promise<T> {
  let url: URL;
  try {
    ({ url } = await getUrl({ path, options: { expiresIn: URL_TTL_SECONDS } }));
  } catch (err) {
    if (isNoSuchKey(err)) throw new SearchIndexReadError(path, 404);
    throw err;
  }
  const response = await fetch(url);
  if (!response.ok) throw new SearchIndexReadError(path, response.status);
  return (await response.json()) as T;
}

function isNoSuchKey(error: unknown): boolean {
  const name = (error as { name?: string } | null)?.name ?? "";
  const message = error instanceof Error ? error.message : String(error);
  return name === "NoSuchKey" || message.includes("NoSuchKey");
}

/**
 * Holds the index in memory for the life of the tab, refetching only once it
 * has outlived the build cadence.
 *
 * The cache is deliberately in-memory rather than `sessionStorage`: the
 * artifact is a couple of megabytes gzipped but `sessionStorage` holds it
 * inflated, which risks a `QuotaExceededError` that would surface as an
 * intermittent page failure rather than a cache miss.
 *
 * Concurrent callers share one in-flight request, so the merchants list and
 * the command palette mounting together fetch once rather than racing.
 */
export class CachingSearchIndexRepository {
  private cached: SearchIndexArtifact | null = null;
  private cachedAt = 0;
  private inFlight: Promise<SearchIndexArtifact> | null = null;

  constructor(
    private readonly fetchArtifact: () => Promise<SearchIndexArtifact> = loadSearchIndexFromStorage,
    private readonly clock: () => number = Date.now,
  ) {}

  /** When the cached build was generated, or null before anything is cached. */
  get generatedAt(): string | null {
    return this.cached?.generatedAt ?? null;
  }

  /** The cached index, fetching it if absent or past the build cadence. */
  async load(): Promise<SearchIndexArtifact> {
    if (this.cached && this.clock() - this.cachedAt < CACHE_TTL_MS) {
      return this.cached;
    }
    return this.refresh();
  }

  /** Fetch unconditionally, ignoring any still-valid cache. */
  async refresh(): Promise<SearchIndexArtifact> {
    this.inFlight ??= this.fetchArtifact()
      .then((artifact) => {
        this.cached = artifact;
        this.cachedAt = this.clock();
        return artifact;
      })
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight;
  }
}

/**
 * The instance production code should use.
 *
 * The cache and in-flight de-duplication live on the instance, so the
 * merchants list and the command palette must share one or they each fetch
 * the artifact separately.
 */
export const searchIndexRepository = new CachingSearchIndexRepository();
